export const SIGNUP_OTP_RESEND_KEYS = {
  email: 'CHRsignup_email',
  password: 'CHRsignup_password',
  step: 'CHRsignup_step',
  resendTimer: 'CHRsignup_resendTimer',
  resendEnabled: 'CHRsignup_resendEnabled',
  otpSentAt: 'CHRsignup_otpSentAt',
  blockedUntil: 'CHRsignup_blockedUntil',
  cooldownUntil: 'CHRsignup_cooldownUntil',
} as const

export const SIGNUP_OTP_CLEANUP_KEYS = Object.values(SIGNUP_OTP_RESEND_KEYS)

export const clearSignupOtpSessionStorage = () => {
  SIGNUP_OTP_CLEANUP_KEYS.forEach(key => sessionStorage.removeItem(key))
}

export const formatBlockedUntil = (date: Date) =>
  date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

export const formatResendTimer = (totalSeconds: number) => {
  const seconds = Math.max(0, Math.floor(totalSeconds))

  if (seconds > 0 && seconds <= 60) {
    return `00:${String(seconds).padStart(2, '0')}`
  }

  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

export type RestoredOtpResendState = {
  email?: string
  password?: string
  signupStep?: 'form' | 'verify'
  resendTimer?: number
  resendEnabled?: boolean
  isBlocked?: boolean
  blockedUntil?: Date | null
}

export const restoreOtpResendFromSessionStorage = (): RestoredOtpResendState => {
  const storedEmail = sessionStorage.getItem(SIGNUP_OTP_RESEND_KEYS.email)
  const storedPassword = sessionStorage.getItem(SIGNUP_OTP_RESEND_KEYS.password)
  const storedStep = sessionStorage.getItem(SIGNUP_OTP_RESEND_KEYS.step)

  const restored: RestoredOtpResendState = {}

  if (storedEmail !== null) restored.email = storedEmail
  if (storedPassword !== null) restored.password = storedPassword
  if (storedStep === 'form' || storedStep === 'verify') {
    restored.signupStep = storedStep
  }

  if (storedStep === 'verify') {
    const blockedUntilStr = sessionStorage.getItem(
      SIGNUP_OTP_RESEND_KEYS.blockedUntil
    )
    if (blockedUntilStr && new Date(blockedUntilStr) > new Date()) {
      restored.isBlocked = true
      restored.blockedUntil = new Date(blockedUntilStr)
      return restored
    }

    const cooldownUntilStr = sessionStorage.getItem(
      SIGNUP_OTP_RESEND_KEYS.cooldownUntil
    )
    if (cooldownUntilStr) {
      const remaining = Math.ceil(
        (new Date(cooldownUntilStr).getTime() - Date.now()) / 1000
      )
      if (remaining > 0) {
        restored.resendTimer = remaining
        restored.resendEnabled = false
      } else {
        restored.resendEnabled = true
      }
    }
  }

  return restored
}

export type OtpSendRateLimitData = {
  isBlocked?: boolean
  blockedUntil?: string
  cooldownUntil?: string
}

export const persistOtpSendSuccess = (cooldownSeconds: number) => {
  const cooldownUntil = new Date(Date.now() + cooldownSeconds * 1000)
  sessionStorage.setItem(
    SIGNUP_OTP_RESEND_KEYS.cooldownUntil,
    cooldownUntil.toISOString()
  )
  sessionStorage.removeItem(SIGNUP_OTP_RESEND_KEYS.blockedUntil)
  sessionStorage.setItem(
    SIGNUP_OTP_RESEND_KEYS.otpSentAt,
    String(Date.now())
  )
}

export const persistOtpSendBlocked = (blockedUntil: Date) => {
  sessionStorage.setItem(
    SIGNUP_OTP_RESEND_KEYS.blockedUntil,
    blockedUntil.toISOString()
  )
  sessionStorage.removeItem(SIGNUP_OTP_RESEND_KEYS.cooldownUntil)
}

export const getRemainingCooldownSeconds = (
  data: OtpSendRateLimitData
): number | null => {
  if (data.cooldownUntil) {
    return Math.ceil(
      (new Date(data.cooldownUntil).getTime() - Date.now()) / 1000
    )
  }
  return null
}

export const getOtpVerifyRateLimitMessage = (
  data?: OtpSendRateLimitData
): string => {
  if (data?.isBlocked) {
    return 'Too many attempts. Try again after 24 hours.'
  }
  return 'Too many attempts. Please request a new code.'
}
